import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Target, ChevronDown, Rocket, Bell, BellOff, TrendingUp, X, Loader2, Search, ExternalLink, CheckCircle } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPositionTrackerList, postPositionTracker, startCrawl } from '../../store/slices/positionTrackerSlice';
import PositionTrackingTable from '../../services/positionTrackerTable';
import AdvancedStatusModal from '../../component/positionTracker/SimpleStatusModal';
import ConfirmDeleteAlert from '../../component/positionTracker/ConfirmDeleteAlert';
import TrackingConfirmModal from '../../component/positionTracker/TrackingConfirmModal';

// Get API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.API_BASE_URL || 'https://mars2.mediacle.net';

// Country options for geolocation
const COUNTRY_OPTIONS = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'DK', name: 'Denmark' },
  { code: 'FI', name: 'Finland' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'IN', name: 'India' },
  { code: 'SG', name: 'Singapore' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'ZA', name: 'South Africa' },
];

// Searchable custom dropdown component that supports images and proper positioning
const SearchableSelectFilter = ({ label, options, selectedValue, onChange, allowCustomInput = false, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = options.find(option => option.value === selectedValue);

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setShowCustomInput(false);
        setCustomValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (value) => {
    onChange(value);
    setIsOpen(false);
    setSearchTerm('');
    setShowCustomInput(false);
    setCustomValue('');
  };

  const handleCustomSubmit = () => {
    if (customValue.trim()) {
      onChange(customValue.trim());
      setIsOpen(false);
      setSearchTerm('');
      setShowCustomInput(false);
      setCustomValue('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && showCustomInput) {
      e.preventDefault();
      handleCustomSubmit();
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
        {label}
      </label>
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-900 dark:text-slate-100 cursor-pointer text-left flex items-center gap-2"
        >
          {selectedOption ? (
            <>
              {selectedOption.icon && (
                <img
                  src={selectedOption.icon}
                  alt=""
                  className="w-4 h-4 rounded"
                  onError={(e) => e.target.style.display = 'none'}
                />
              )}
              <span className="truncate">{selectedOption.name}</span>
            </>
          ) : selectedValue && !selectedOption ? (
            <span className="truncate text-gray-800 dark:text-slate-200">{selectedValue}</span>
          ) : (
            <span className="text-gray-500 dark:text-slate-400">{placeholder || label}</span>
          )}

          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <ChevronDown
              className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
                isOpen ? 'rotate-180' : 'rotate-0'
              }`}
            />
          </div>
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
            <div className="p-2 border-b border-gray-100 dark:border-slate-700">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={`Search ${label.toLowerCase()}...`}
                value={showCustomInput ? customValue : searchTerm}
                onChange={(e) => {
                  if (showCustomInput) {
                    setCustomValue(e.target.value);
                  } else {
                    setSearchTerm(e.target.value);
                  }
                }}
                onKeyPress={handleKeyPress}
                className="w-full px-2 py-1 border border-gray-200 dark:border-slate-700 rounded text-sm bg-white dark:bg-slate-800 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            {!showCustomInput ? (
              <>
                {allowCustomInput && (
                  <div
                    onClick={() => setShowCustomInput(true)}
                    className="px-3 py-2 hover:bg-blue-50 dark:hover:bg-slate-800 cursor-pointer flex items-center gap-2 text-blue-600 dark:text-blue-400 border-b border-gray-100 dark:border-slate-700"
                  >
                    <span>+ Add custom URL</span>
                  </div>
                )}

                <div
                  onClick={() => handleSelect('')}
                  className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer flex items-center gap-2 text-gray-500 dark:text-slate-400"
                >
                  {placeholder || label}
                </div>

                {filteredOptions.map((option, index) => (
                  <div
                    key={index}
                    onClick={() => handleSelect(option.value)}
                    className="px-3 py-2 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer flex items-center gap-2 border-t border-gray-100 dark:border-slate-700 dark:text-slate-200"
                  >
                    {option.icon && (
                      <img
                        src={option.icon}
                        alt=""
                        className="w-4 h-4 rounded flex-shrink-0"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                    <span className="truncate">{option.name}</span>
                  </div>
                ))}

                {filteredOptions.length === 0 && searchTerm && (
                  <div className="px-3 py-2 text-gray-500 dark:text-slate-400 text-sm">
                    No results found
                  </div>
                )}
              </>
            ) : (
              <div className="p-3">
                <label className="block text-xs text-gray-600 dark:text-slate-400 mb-1">Enter custom URL:</label>
                <div className="flex gap-2">
                  <button
                    onClick={handleCustomSubmit}
                    disabled={!customValue.trim()}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomValue('');
                    }}
                    className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const PositionTracker = () => {
  // COMING SOON MODE - Set to false to re-enable functionality
  const COMING_SOON_MODE = false;

  const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
  const dispatch = useDispatch();
  const { trackingList, loading, error } = useSelector((state) => state.positionTracker);

  const [selectedBrands, setSelectedBrands] = useState('');
  const [affiliateUrl, setAffiliateUrl] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('US');
  const [serpPages, setSerpPages] = useState([]);
  const [isFindingPages, setIsFindingPages] = useState(false);
  const [selectedPageUrl, setSelectedPageUrl] = useState('');
  const [showCustomUrl, setShowCustomUrl] = useState(false);
  const [customPageUrl, setCustomPageUrl] = useState('');
  const [brandUser, setBrandUser] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);

  // Affiliate URL verification state
  const [affiliateVerified, setAffiliateVerified] = useState(false);
  const [affiliateVerifyMsg, setAffiliateVerifyMsg] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalBrandName, setModalBrandName] = useState('');
  const [modalAffiliateUrl, setModalAffiliateUrl] = useState('');
  const [modalTrackingId, setModalTrackingId] = useState(null);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteBrandName, setDeleteBrandName] = useState('');

  // Tracking modal state
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [trackingData, setTrackingData] = useState({
    id: null,
    brandName: '',
    affiliateUrl: '',
    currentPosition: null
  });

  // Tracking usage/limits state
  const [trackingUsage, setTrackingUsage] = useState(null);

  // Position change notifications state
  const [positionNotifications, setPositionNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const notificationRef = useRef(null);

  // Fetch position change notifications
  const fetchPositionNotifications = useCallback(async () => {
    try {
      setIsLoadingNotifications(true);

      const userResponse = await fetch(`${API_BASE_URL}/api/auth/me`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!userResponse.ok) return;

      const userData = await userResponse.json();
      if (!userData.success || !userData.data?.id) return;

      const userId = userData.data.id;

      const response = await fetch(`${API_BASE_URL}/api/notification`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Filter only position_change notifications
          const positionChanges = result.data
            .filter(n => n.notification_type === 'position_change')
            .map(n => {
              let notificationData = null;
              try {
                notificationData = typeof n.notification_data === 'string'
                  ? JSON.parse(n.notification_data)
                  : n.notification_data;
              } catch (e) {
                console.error('Error parsing notification_data:', e);
              }

              return {
                id: n.id,
                brandName: n.brand_name,
                message: n.message,
                createdAt: n.created_at,
                status: n.read ? 'read' : 'unread',
                oldPosition: notificationData?.old_position || 0,
                newPosition: notificationData?.new_position || 0,
                positionChange: notificationData?.position_change || 0,
                country: notificationData?.country || 'N/A',
                isImprovement: notificationData?.is_improvement || false,
                timeAgo: formatNotificationTime(n.created_at)
              };
            });

          setPositionNotifications(positionChanges);
        }
      }
    } catch (error) {
      console.error('Error fetching position notifications:', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, []);

  // Format notification time
  const formatNotificationTime = (createdAt) => {
    try {
      const now = new Date();
      const notificationTime = new Date(createdAt);

      if (isNaN(notificationTime.getTime())) return 'Recently';

      const diffInMinutes = Math.floor((now - notificationTime) / (1000 * 60));

      if (diffInMinutes < 1) return 'Just now';
      if (diffInMinutes < 60) return `${diffInMinutes} min ago`;

      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;

      return notificationTime.toLocaleDateString();
    } catch (error) {
      return 'Recently';
    }
  };

  // Delete notification
  const deleteNotification = useCallback(async (notificationId, event) => {
    if (event) event.stopPropagation();

    try {
      const response = await fetch(`${API_BASE_URL}/api/notification/${notificationId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        setPositionNotifications(prev => prev.filter(n => n.id !== notificationId));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, []);

  // Handle click outside to close notifications
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showNotifications]);

  // Fetch tracking usage
  const fetchTrackingUsage = async () => {
    try {
      const response = await fetch(`${basePath}/api/position-tracker/tracking/usage`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setTrackingUsage(result.data);
        }
      }
    } catch (error) {
      console.error('Error fetching tracking usage:', error);
    }
  };

  // Fetch brands data
  const fetchData = async () => {
    try {
      const response = await fetch(`${basePath}/api/position-tracker`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setBrandUser(data);
      } else {
        console.error('API response not ok:', response.status);
      }
    } catch (error) {
      console.error('Error fetching brand data:', error);
    }
  };

  // Find pages - first verifies URL is affiliate, then searches for pages
  const handleFindPages = async () => {
    if (!selectedBrands?.toString().trim() || !affiliateUrl?.toString().trim()) {
      alert('Please select a brand and enter an affiliate URL first.');
      return;
    }

    const trimmedUrl = affiliateUrl.trim();
    const selectedBrand = brandUser.find(b => b.brand_url === selectedBrands);
    const brandName = selectedBrand?.brand_name || '';

    setIsFindingPages(true);
    setAffiliateVerifyMsg('');
    setSerpPages([]);
    setSelectedPageUrl('');
    setShowCustomUrl(false);
    setCustomPageUrl('');

    // Step 1: Verify URL is an affiliate site (if not already verified)
    if (!affiliateVerified) {
      const normalizedUrl = trimmedUrl.startsWith('http') ? trimmedUrl : 'https://' + trimmedUrl;

      try {
        const verifyResponse = await fetch(`${basePath}/api/brandAddition/verify-affiliate-url`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: normalizedUrl })
        });

        const verifyData = await verifyResponse.json();

        if (!verifyData.success || !verifyData.isAffiliate) {
          setAffiliateVerified(false);
          setAffiliateVerifyMsg(verifyData.message || 'This URL does not appear to be a gambling affiliate site.');
          setIsFindingPages(false);
          return;
        }

        setAffiliateVerified(true);
        setAffiliateVerifyMsg('Verified as affiliate/review site.');
      } catch (err) {
        setAffiliateVerified(false);
        setAffiliateVerifyMsg('Failed to verify URL. Please try again.');
        setIsFindingPages(false);
        return;
      }
    }

    // Step 2: Find pages mentioning the brand on the affiliate site
    try {
      const response = await fetch(`${API_BASE_URL}/api/position-tracker/find-pages`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          affiliateUrl: trimmedUrl,
          brandName,
          brandUrl: selectedBrands,
          country: selectedCountry
        })
      });

      const data = await response.json();

      if (data.success && data.results?.length > 0) {
        setSerpPages(data.results);
      } else {
        setSerpPages([]);
        setShowCustomUrl(true);
      }
    } catch (err) {
      console.error('Error finding pages:', err);
      setSerpPages([]);
      setShowCustomUrl(true);
    } finally {
      setIsFindingPages(false);
    }
  };

  const handleAddForTracking = async () => {
    // Check if user has reached their limit
    if (trackingUsage && trackingUsage.current >= trackingUsage.limit) {
      let message = `❌ Position tracking limit reached for ${trackingUsage.plan} plan\n\n`;
      message += `You have ${trackingUsage.current} of ${trackingUsage.limit} position tracking entries.\n\n`;
      message += `Please upgrade your plan or remove existing entries to add more.`;
      alert(message);
      return;
    }

    // Determine the final tracking URL (selected page or custom or raw affiliate URL)
    const finalTrackingUrl = selectedPageUrl || customPageUrl?.trim() || affiliateUrl?.toString().trim();

    if (!selectedBrands?.toString().trim() || !finalTrackingUrl) {
      alert('Please select a brand and a page to track.');
      return;
    }

    // Get brand name for modal
    const selectedBrand = brandUser.find(b => b.brand_url === selectedBrands);
    const brandName = selectedBrand?.brand_name || 'Brand';

    // Generate a tracking ID for this crawl
    const trackingId = `track_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Show modal immediately
    setModalBrandName(brandName);
    setModalAffiliateUrl(affiliateUrl);
    setModalTrackingId(trackingId);
    setShowModal(true);
    setIsAdding(true);
    setAddError('');

    try {
      // Get country name from code
      const selectedCountryObj = COUNTRY_OPTIONS.find(c => c.code === selectedCountry);
      const countryName = selectedCountryObj?.name || 'United States';

      // Add tracking with the selected/custom page URL
      const result = await dispatch(
        postPositionTracker({
          selectedBrands,
          selectedAffiliate: finalTrackingUrl,
          brandName: brandName,
          country: countryName,
          countryCode: selectedCountry
        })
      ).unwrap();


      // Refresh list
      await dispatch(fetchPositionTrackerList()).unwrap();

      // Get brand details from result
      const row = result?.results?.[0]?.row;
      const brand_name = row?.brand_name || brandName;
      const brand_url = row?.brand_url || selectedBrands;
      const affiliate_url = row?.affiliate_url || affiliateUrl;
      const user_id = row?.user_id;
      const position_id = row?.id;

      // Start crawl with tracking ID and country (fire and forget)
      if (brand_name && brand_url && affiliate_url) {

        dispatch(startCrawl({
          G_Brand_Name: brand_name,
          G_Brand_Url: brand_url,
          G_Affiliate_Url: affiliate_url,
          G_User_Id: user_id,
          G_Brande_Id: position_id,
          trackingId,
          country: countryName,
          countryCode: selectedCountry
        }))
          .unwrap()
          .then(() => {
          })
          .catch(err => {
            console.warn('Crawl failed:', err);

            // Display detailed error message
            let errorMessage = '';

            if (typeof err === 'object' && err.message) {
              errorMessage = err.message;

              // Add suggestions if available
              if (err.suggestions && err.suggestions.length > 0) {
                errorMessage += '\n\nSuggestions:\n';
                err.suggestions.forEach((suggestion, index) => {
                  errorMessage += `${index + 1}. ${suggestion}\n`;
                });
              }

              // Add brand and URL info if available
              if (err.data) {
                errorMessage += `\n📍 Brand: ${err.data.brandName || brand_name}`;
                errorMessage += `\n🔗 Affiliate URL: ${err.data.affiliateUrl || affiliate_url}`;
              }
            } else {
              errorMessage = err?.toString() || 'Failed to start crawl';
            }

            alert(errorMessage);
          });
      }

      // Reset selections
      setSelectedBrands('');
      setAffiliateUrl('');
      setSelectedCountry('US');

    } catch (error) {
      console.error('Error adding tracking:', error);
      // Close modal on error
      setShowModal(false);

      // Check if it's a plan limit error from backend
      if (error?.error === 'POSITION_LIMIT_REACHED' && error?.details) {
        let message = `❌ ${error.message}\n\n`;
        message += `Plan: ${error.details.currentPlan}\n`;
        message += `Limit: ${error.details.limit} total entries\n`;
        message += `Current: ${error.details.current} entries\n\n`;

        if (error.suggestions && error.suggestions.length > 0) {
          message += 'Suggestions:\n';
          error.suggestions.forEach((suggestion, index) => {
            message += `${index + 1}. ${suggestion}\n`;
          });
        }
        alert(message);
        fetchTrackingUsage(); // Refresh usage
      } else {
        // Show the actual error message (e.g. duplicate page error)
        const errorMsg = typeof error === 'string' ? error : (error?.message || 'Error adding tracking. Please try again.');
        setAddError(errorMsg);
      }
    } finally {
      setIsAdding(false);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowModal(false);
    // Refresh table when modal closes
    dispatch(fetchPositionTrackerList());
  };

  // Handle delete click
  const handleDeleteClick = (id, brandName) => {
    setDeleteId(id);
    setDeleteBrandName(brandName);
    setShowDeleteModal(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${basePath}/api/position-tracker/remove/${deleteId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Refresh the list and usage
        dispatch(fetchPositionTrackerList());
        fetchTrackingUsage();
        setShowDeleteModal(false);
        setDeleteId(null);
        setDeleteBrandName('');
      } else {
        console.error('Failed to delete position tracker');
        alert('Failed to delete the position tracker. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting position tracker:', error);
      alert('Error deleting the position tracker. Please try again.');
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
    setDeleteBrandName('');
  };

  // Handle start tracking click
  const handleStartTrackingClick = (row) => {
    setTrackingData({
      id: row.id,
      brandName: row.brand_name,
      affiliateUrl: row.affiliate_url,
      currentPosition: row.position
    });
    setShowTrackingModal(true);
  };

  // Handle tracking confirmation
  const handleTrackingConfirm = async (emailNotifications) => {
    try {
      const response = await fetch(`${basePath}/api/position-tracker/tracking/enable`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: trackingData.id,
          trackingFrequency: 0.0167, // 1 minute
          emailNotifications
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(`✅ Tracking enabled successfully!\n\nPosition will be checked every 1 minute${emailNotifications ? ' with email notifications' : ''}.`);
        // Refresh the list and usage
        dispatch(fetchPositionTrackerList());
        fetchTrackingUsage();
        setShowTrackingModal(false);
      } else if (response.status === 403 && result.error === 'TRACKING_LIMIT_REACHED') {
        // Plan limit reached
        let message = `❌ ${result.message}\n\n`;
        message += `Plan: ${result.details.currentPlan}\n`;
        message += `Limit: ${result.details.limit} active tracking entries\n`;
        message += `Current: ${result.details.current} active\n\n`;

        if (result.suggestions && result.suggestions.length > 0) {
          message += 'Suggestions:\n';
          result.suggestions.forEach((suggestion, index) => {
            message += `${index + 1}. ${suggestion}\n`;
          });
        }

        alert(message);
        setShowTrackingModal(false);
      } else {
        alert(`Failed to enable tracking: ${result.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error enabling tracking:', error);
      alert('Error enabling tracking. Please try again.');
    }
  };

  // Handle stop tracking click
  const handleStopTrackingClick = async (id, brandName) => {
    if (window.confirm(`Stop automated tracking for "${brandName}"?\n\nYou will no longer receive automatic position updates.`)) {
      try {
        const response = await fetch(`${basePath}/api/position-tracker/tracking/disable`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ id })
        });

        const result = await response.json();

        if (response.ok && result.success) {
          alert('✅ Automated tracking disabled successfully.');
          // Refresh the list and usage to show updated tracking status
          dispatch(fetchPositionTrackerList());
          fetchTrackingUsage();
        } else {
          alert(`Failed to disable tracking: ${result.message || 'Unknown error'}`);
        }
      } catch (error) {
        console.error('Error disabling tracking:', error);
        alert('Error disabling tracking. Please try again.');
      }
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      try {
        await fetchData();
        await dispatch(fetchPositionTrackerList()).unwrap();
        await fetchTrackingUsage();
      } catch (error) {
        console.error('Error loading initial data:', error);
      } finally {
        setInitialLoading(false);
      }
    };

    loadInitialData();
  }, [dispatch]);

  // Fetch position notifications on mount and set up auto-refresh
  useEffect(() => {
    fetchPositionNotifications();

    // Auto-refresh notifications every 30 seconds
    const intervalId = setInterval(() => {
      fetchPositionNotifications();
    }, 30000);

    return () => clearInterval(intervalId);
  }, [fetchPositionNotifications]);

  const filterOptions = {
    existingAffiliate: Array.isArray(brandUser)
      ? brandUser.map(brand => ({
          name: brand.brand_name,
          value: brand.brand_url,
          icon: brand.brand_icon || brand.icon
        }))
      : []
  };

  const isFormValid = () => {
    return selectedBrands &&
           affiliateUrl &&
           selectedBrands.toString().trim() !== '' &&
           affiliateUrl.toString().trim() !== '';
  };

  const clearAllFilters = () => {
    setSelectedBrands('');
    setAffiliateUrl('');
    setSelectedCountry('US');
    setSerpPages([]);
    setSelectedPageUrl('');
    setShowCustomUrl(false);
    setCustomPageUrl('');
  };

  const columns = useMemo(() => [
    {
      header: 'Brand',
      accessorKey: 'brand_name',
      cell: (info) => info.getValue() || 'N/A'
    },
    {
      header: 'Affiliate URL',
      accessorKey: 'affiliate_url',
      cell: (info) => (
        <div className="max-w-xs truncate" title={info.getValue()}>
          {info.getValue() || 'N/A'}
        </div>
      )
    },
    {
      header: 'Position & Offer',
      accessorKey: 'position',
      cell: (info) => {
        const positionValue = info.getValue();
        const offerValue = info.row.original.offer_text;

        // Function to format offer text with line breaks
        const formatOfferText = (text) => {
          if (!text) return null;

          // Split at natural break points (after 50-60 characters at word boundaries)
          const words = text.split(' ');
          const lines = [];
          let currentLine = '';

          words.forEach((word, index) => {
            const testLine = currentLine ? `${currentLine} ${word}` : word;

            // Break line if it exceeds ~50 characters or at certain keywords
            if (testLine.length > 50 ||
                (currentLine.length > 30 && ['when', 'with', 'plus', 'and'].includes(word.toLowerCase()))) {
              if (currentLine) {
                lines.push(currentLine);
                currentLine = word;
              } else {
                currentLine = testLine;
              }
            } else {
              currentLine = testLine;
            }

            // Push the last line
            if (index === words.length - 1 && currentLine) {
              lines.push(currentLine);
            }
          });

          return lines;
        };

        const offerLines = offerValue ? formatOfferText(offerValue) : null;

        return (
          <div className="flex items-start gap-3 max-w-sm">
            {/* Position in round div */}
            <div className="flex-shrink-0">
              {positionValue !== null && positionValue !== undefined ? (
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                  <span className="text-white font-bold text-sm">#{positionValue}</span>
                </div>
              ) : (
                <div className="w-10 h-10 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                  <span className="text-yellow-800 dark:text-yellow-200 font-semibold text-xs">...</span>
                </div>
              )}
            </div>

            {/* Offer text */}
            <div className="flex-1 min-w-0 overflow-hidden">
              {offerLines ? (
                <div className="w-full" title={offerValue}>
                  <div className="px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md">
                    <div className="text-xs leading-relaxed">
                      <span className="font-semibold text-gray-700 dark:text-slate-300">Offer: </span>
                      <span className="font-medium text-gray-800 dark:text-slate-200">
                        {offerLines.map((line, idx) => (
                          <React.Fragment key={idx}>
                            {line}
                            {idx < offerLines.length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <span className="text-xs text-gray-400 dark:text-slate-500">No offer</span>
              )}
            </div>
          </div>
        );
      }
    },
    {
      header: 'Country',
      accessorKey: 'country',
      cell: (info) => {
        const country = info.getValue();
        const countryCode = info.row.original.country_code;
        return country ? (
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gray-700 dark:text-slate-300">{country}</span>
            {countryCode && (
              <span className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded text-xs font-medium">
                {countryCode}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-gray-400 dark:text-slate-500">N/A</span>
        );
      }
    },
    {
      header: 'Last Updated',
      accessorKey: 'last_updated',
      cell: (info) => {
        const value = info.getValue() || info.row.original.date;
        if (!value) return 'N/A';
        const date = new Date(value);
        return (
          <div className="text-xs">
            <div className="font-medium text-gray-700 dark:text-slate-300">
              {date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              })}
            </div>
            <div className="text-gray-500 dark:text-slate-400">
              {date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </div>
          </div>
        );
      }
    },
    {
      header: 'Tracking',
      accessorKey: 'is_tracking_enabled',
      cell: (info) => {
        const isTrackingEnabled = info.getValue();
        const row = info.row.original;
        const rowId = row.id;
        const brandName = row.brand_name;
        const nextTrackingAt = row.next_tracking_at;

        if (isTrackingEnabled) {
          return (
            <div className="space-y-1">
              <button
                onClick={() => handleStopTrackingClick(rowId, brandName)}
                className="w-full px-3 py-1.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
              >
                <BellOff className="w-3.5 h-3.5" />
                Stop Tracking
              </button>
              {nextTrackingAt && (
                <p className="text-xs text-gray-500 dark:text-slate-400 text-center">
                  Next: {new Date(nextTrackingAt).toLocaleString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              )}
            </div>
          );
        }

        return (
          <button
            onClick={() => handleStartTrackingClick(row)}
            className="w-full px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
          >
            <Bell className="w-3.5 h-3.5" />
            Start Tracking
          </button>
        );
      }
    },
    {
      header: 'Actions',
      accessorKey: 'id',
      cell: (info) => {
        const rowId = info.getValue();
        const brandName = info.row.original.brand_name;
        return (
          <button
            onClick={() => handleDeleteClick(rowId, brandName)}
            className="px-3 py-1 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-md text-xs font-medium transition-colors"
          >
            Remove
          </button>
        );
      }
    },
  ], []);

  // Render coming soon banner if enabled
  if (COMING_SOON_MODE) {
    return (
      <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950">
        {/* Header */}
        <div className="mb-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100">
                  Position Tracker
                </h1>
                <p className="text-gray-600 dark:text-slate-400 leading-relaxed max-w-xl">
                  Track your brand's position across live affiliate websites in real-time
                </p>
              </div>
              <div className="flex items-center gap-4">
                <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg border border-blue-200 dark:border-blue-700 shadow-sm">
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Premium Tools</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coming Soon Banner */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 dark:from-blue-900/20 dark:via-purple-900/20 dark:to-pink-900/20 rounded-xl border-2 border-blue-300 dark:border-blue-700 shadow-lg p-8">
            <div className="flex flex-col items-center justify-center text-center">
              <div className="bg-blue-100 dark:bg-blue-900 rounded-full p-4 mb-4">
                <Rocket className="w-12 h-12 text-blue-600 dark:text-blue-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-slate-100 mb-3">
                Coming Soon!
              </h2>
              <p className="text-gray-700 dark:text-slate-300 text-lg mb-2 max-w-2xl">
                We're working hard to bring you an amazing Position Tracker experience with enhanced features and improved performance.
              </p>
              <p className="text-gray-600 dark:text-slate-400 mb-4">
                Stay tuned for the launch!
              </p>
              <div className="mt-4 px-6 py-3 bg-white dark:bg-slate-800 rounded-lg border border-blue-200 dark:border-blue-700 shadow-sm">
                <p className="text-sm text-gray-600 dark:text-slate-400">
                  Launching soon – get ready to track your brand positions like never before
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Read-only table - still show existing data */}
        <div className="mb-6 opacity-75 pointer-events-none">
          <PositionTrackingTable
            data={trackingList || []}
            columns={columns}
            title="Brand Position Tracking (Preview)"
            searchable={false}
            defaultPageSize={10}
            loading={loading}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950">

      {/* Status Modal */}
      <AdvancedStatusModal
        isOpen={showModal}
        onClose={handleModalClose}
        brandName={modalBrandName}
        affiliateUrl={modalAffiliateUrl}
        trackingId={modalTrackingId}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteAlert
        isOpen={showDeleteModal}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        brandName={deleteBrandName}
      />

      {/* Tracking Confirmation Modal */}
      <TrackingConfirmModal
        isOpen={showTrackingModal}
        onClose={() => setShowTrackingModal(false)}
        onConfirm={handleTrackingConfirm}
        brandName={trackingData.brandName}
        affiliateUrl={trackingData.affiliateUrl}
        currentPosition={trackingData.currentPosition}
      />

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100">
                Position Tracker
              </h1>
              <p className="text-gray-600 dark:text-slate-400 leading-relaxed max-w-xl">
                Track your brand's position across live affiliate websites in real-time
              </p>
            </div>
            <div className="flex items-center gap-4">
              {trackingUsage && (
                <div className="px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm">
                  <div className="text-xs text-gray-500 dark:text-slate-400 mb-1">Tracking Slots</div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm font-bold text-gray-800 dark:text-slate-100">
                      {trackingUsage.current} / {trackingUsage.limit}
                    </div>
                    <div className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase">
                      {trackingUsage.plan}
                    </div>
                  </div>
                  {trackingUsage.current >= trackingUsage.limit && (
                    <div className="text-xs text-red-600 dark:text-red-400 mt-1">Limit reached</div>
                  )}
                </div>
              )}

              {/* Position Tracker Notifications */}
              <div className="relative" ref={notificationRef}>
                <button
                  onClick={() => {
                    setShowNotifications(!showNotifications);
                    if (!showNotifications) {
                      fetchPositionNotifications();
                    }
                  }}
                  className="relative p-2.5 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm transition-colors"
                  title="Position change notifications"
                >
                  <Bell className="w-5 h-5 text-gray-700 dark:text-slate-300" />
                  {positionNotifications.filter(n => n.status === 'unread').length > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center border-2 border-white">
                      {positionNotifications.filter(n => n.status === 'unread').length}
                    </span>
                  )}
                </button>

                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-96 bg-white dark:bg-slate-900 rounded-lg shadow-xl border border-gray-200 dark:border-slate-700 z-50 max-h-[500px] overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between bg-gray-50 dark:bg-slate-800">
                      <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Position Changes</h3>
                      <button
                        onClick={() => setShowNotifications(false)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded transition-colors"
                      >
                        <X className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                      </button>
                    </div>

                    {/* Notifications List */}
                    <div className="overflow-y-auto flex-1">
                      {isLoadingNotifications ? (
                        <div className="flex items-center justify-center py-12">
                          <Loader2 className="w-6 h-6 text-gray-400 dark:text-slate-500 animate-spin" />
                        </div>
                      ) : positionNotifications.length === 0 ? (
                        <div className="py-12 px-4 text-center">
                          <Bell className="w-12 h-12 text-gray-300 dark:text-slate-600 mx-auto mb-3" />
                          <p className="text-sm text-gray-500 dark:text-slate-400">No position changes yet</p>
                          <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">You'll be notified when your brand positions change</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-gray-100 dark:divide-slate-700">
                          {positionNotifications.map((notification) => (
                            <div
                              key={notification.id}
                              className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors ${
                                notification.status === 'unread' ? 'bg-blue-50 dark:bg-slate-800' : ''
                              }`}
                            >
                              <div className="flex items-start gap-3">
                                {/* Position Change Icon */}
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center mt-1 flex-shrink-0 ${
                                  notification.isImprovement ? 'bg-green-100 dark:bg-green-900' : 'bg-orange-100 dark:bg-orange-900'
                                }`}>
                                  <TrendingUp className={`w-4 h-4 ${
                                    notification.isImprovement ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400 rotate-180'
                                  }`} />
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="text-sm font-medium text-gray-900 dark:text-slate-100 line-clamp-1">
                                        {notification.brandName}
                                      </p>
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        notification.isImprovement
                                          ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                                          : 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300'
                                      }`}>
                                        {notification.isImprovement ? '↑ Improved' : '↓ Dropped'}
                                      </span>
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deleteNotification(notification.id);
                                      }}
                                      className="p-1 hover:bg-gray-200 dark:hover:bg-slate-700 rounded transition-colors flex-shrink-0"
                                      title="Delete notification"
                                    >
                                      <X className="w-3 h-3 text-gray-400 dark:text-slate-500" />
                                    </button>
                                  </div>

                                  {/* Position Display */}
                                  <div className="mt-2 space-y-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-gray-500 dark:text-slate-400 font-medium">Position:</span>
                                      <div className="flex items-center gap-1.5">
                                        <span className="font-mono text-base font-semibold text-gray-700 dark:text-slate-300">
                                          #{notification.oldPosition}
                                        </span>
                                        <span className="text-gray-400 dark:text-slate-500">→</span>
                                        <span className={`font-mono text-base font-semibold ${
                                          notification.isImprovement ? 'text-green-600 dark:text-green-400' : 'text-orange-600 dark:text-orange-400'
                                        }`}>
                                          #{notification.newPosition}
                                        </span>
                                      </div>
                                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                        notification.isImprovement
                                          ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                                          : 'bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300'
                                      }`}>
                                        {notification.positionChange > 0 ? '+' : ''}{notification.positionChange}
                                      </span>
                                    </div>
                                    <p className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1">
                                      <span className="inline-block w-1 h-1 rounded-full bg-gray-400 dark:bg-slate-500"></span>
                                      {notification.country}
                                    </p>
                                  </div>

                                  {/* Timestamp */}
                                  <p className="text-xs text-gray-400 dark:text-slate-500">
                                    {notification.timeAgo}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-lg border border-blue-200 dark:border-blue-700 shadow-sm">
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">Premium Tools</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Add Tracking Section */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Add New Tracking</h2>
            </div>

            <button
              onClick={clearAllFilters}
              className="px-4 py-2 text-sm text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-slate-200 border border-gray-200 dark:border-slate-700 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
            >
              Clear All
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Brand Selection */}
            <div className="relative">
              <SearchableSelectFilter
                label="Select Brand"
                options={filterOptions.existingAffiliate}
                selectedValue={selectedBrands}
                onChange={setSelectedBrands}
                placeholder="Select Brand"
              />
            </div>

            {/* Affiliate URL Text Input with Auto-Verify */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Affiliate URL
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={affiliateUrl}
                  onChange={(e) => {
                    setAffiliateUrl(e.target.value);
                    setAffiliateVerified(false);
                    setAffiliateVerifyMsg('');
                    setSerpPages([]);
                    setSelectedPageUrl('');
                    setShowCustomUrl(false);
                  }}
                  placeholder="e.g., oddschecker.com"
                  className={`w-full px-3 py-2 pr-9 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-slate-100 ${
                    affiliateVerified ? 'border-green-400 dark:border-green-600' : affiliateVerifyMsg && !affiliateVerified ? 'border-red-300 dark:border-red-600' : 'border-gray-300 dark:border-slate-700'
                  }`}
                />
                {affiliateVerified && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                )}
              </div>
              {affiliateVerifyMsg && (
                <p className={`absolute left-0 top-full mt-1 text-xs flex items-center gap-1 ${affiliateVerified ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {affiliateVerifyMsg}
                </p>
              )}
            </div>

            {/* Country Selection */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Target Country
              </label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-slate-100"
              >
                {COUNTRY_OPTIONS.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Find Pages Button - verifies URL on click, then finds pages */}
            <div className="flex items-end">
              <button
                onClick={handleFindPages}
                disabled={!selectedBrands?.toString().trim() || !affiliateUrl?.toString().trim() || isFindingPages}
                className={`w-full px-4 py-2 rounded-lg transition-colors font-medium ${
                  !selectedBrands?.toString().trim() || !affiliateUrl?.toString().trim() || isFindingPages
                    ? 'bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-slate-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {isFindingPages ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {!affiliateVerified ? 'Checking URL...' : 'Finding Pages...'}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Search className="w-4 h-4" />
                    Find Pages
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* SERP Results - Page Selection */}
          {(serpPages.length > 0 || showCustomUrl) && (
            <div className="mt-6 border-t border-gray-200 dark:border-slate-700 pt-4">

              <h3 className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
                Select a page to track
              </h3>

              <div className="space-y-2 max-h-80 overflow-y-auto mb-4">
                {/* Custom URL as first radio option */}
                <label
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    customPageUrl
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500'
                      : 'border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-gray-50 dark:hover:bg-slate-800'
                  }`}
                >
                  <input
                    type="radio"
                    name="trackingPage"
                    checked={!!customPageUrl}
                    onChange={() => { setSelectedPageUrl(''); }}
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-slate-100 mb-1.5">Custom URL</p>
                    <input
                      type="text"
                      value={customPageUrl}
                      onChange={(e) => { setCustomPageUrl(e.target.value); setSelectedPageUrl(''); }}
                      onFocus={() => setSelectedPageUrl('')}
                      placeholder="e.g., https://oddschecker.com/casino/swift-casino-review"
                      className="w-full px-3 py-1.5 border border-gray-300 dark:border-slate-600 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-slate-800 dark:text-slate-100 text-sm"
                    />
                  </div>
                </label>

                {/* SERP results as radio options */}
                {serpPages.map((page, index) => (
                  <label
                    key={index}
                    className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      selectedPageUrl === page.url
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500'
                        : 'border-gray-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-gray-50 dark:hover:bg-slate-800'
                    }`}
                  >
                    <input
                      type="radio"
                      name="trackingPage"
                      checked={selectedPageUrl === page.url && !customPageUrl}
                      onChange={() => { setSelectedPageUrl(page.url); setCustomPageUrl(''); }}
                      className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-slate-100 truncate">{page.title}</p>
                      <p className="text-xs text-blue-600 dark:text-blue-400 truncate mt-0.5">{page.url}</p>
                      {page.description && (
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1 line-clamp-2">{page.description}</p>
                      )}
                    </div>
                    <a
                      href={page.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 flex-shrink-0 mt-1"
                      title="Visit page"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </label>
                ))}
              </div>

              {/* Confirm button */}
              <button
                onClick={handleAddForTracking}
                disabled={(!selectedPageUrl && !customPageUrl?.trim()) || isAdding || (trackingUsage && trackingUsage.current >= trackingUsage.limit)}
                className={`w-full px-6 py-2.5 rounded-lg transition-colors font-medium ${
                  (!selectedPageUrl && !customPageUrl?.trim()) || isAdding || (trackingUsage && trackingUsage.current >= trackingUsage.limit)
                    ? 'bg-gray-300 dark:bg-slate-700 text-gray-500 dark:text-slate-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {trackingUsage && trackingUsage.current >= trackingUsage.limit
                  ? 'Limit Reached'
                  : isAdding
                    ? 'Adding...'
                    : `Confirm & Add For Tracking${selectedPageUrl ? '' : customPageUrl?.trim() ? '' : ''}`}
              </button>
              {addError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">{addError}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tracking Data Table */}
      <div className="mb-6">
        {initialLoading ? (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-12">
            <div className="flex flex-col items-center justify-center">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue-200 dark:border-blue-900 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin"></div>
              </div>
              <p className="mt-4 text-gray-600 dark:text-slate-300 font-medium">Loading position tracking data...</p>
              <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">Please wait while we fetch your tracking information</p>
            </div>
          </div>
        ) : (
          <PositionTrackingTable
            data={trackingList || []}
            columns={columns}
            title="Brand Position Tracking"
            searchable={true}
            defaultPageSize={10}
            loading={loading || isAdding}
          />
        )}
      </div>
    </div>
  );
};

export default PositionTracker;